import { t } from "i18next";

// Generates and returns a date time string formatted as follows:
// “24pv”             exactly 24 days
// "18pv 20:15:22"    18 days, 20 hours, 15 mins and 22 secs
// "1pv 2:24:04"      1 day, 2 hours, 24 mins and 4 secs
// "23:58:11"         23 hours, 58 mins and 11 secs
// "6:09:01"          6 hours, 9 mins and 1 sec
// "47:56"            47 mins and 56 secs
// "5:02"             5 mins and 2 secs
import { isDate } from "date-fns";

/**
 * Safely formats a date string to Finnish locale format.
 * Returns a fallback message if the date is invalid, null, undefined, or empty.
 * @param dateString - The date string to format (can be string, null, or undefined)
 * @param fallback - Optional fallback string if date is invalid
 * @returns Formatted date string or fallback
 */
export const formatDateSafely = (
  dateString: string | undefined | null,
  fallback = "Invalid date"
) => {
  // Check for null, undefined, or empty string
  if (!dateString || dateString.trim() === "") {
    return fallback;
  }

  try {
    const date = new Date(dateString);
    if (!isDate(date) || isNaN(date.getTime())) {
      return fallback;
    }

    return date.toLocaleString("fi-FI", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch (error) {
    return fallback;
  }
};

// "9s"               9 secs
export const secondsToTimeString = (
  secondsValue: number,
  localization: {
    daysUnit: string;
  }
): string => {
  const { daysUnit } = localization;
  let retValue = "0s";
  if (secondsValue > 0) {
    const days = Math.floor(secondsValue / 86400);
    const hours = Math.floor((secondsValue - days * 86400) / 3600);
    const minutes = Math.floor(
      (secondsValue - days * 86400 - hours * 3600) / 60
    );
    const seconds = secondsValue - days * 86400 - hours * 3600 - minutes * 60;
    let timeString = "";
    const timeSeconds = secondsValue - days * 86400;
    if (timeSeconds < 60) {
      if (!(days > 0 && timeSeconds === 0)) {
        timeString = `${seconds}s`;
      }
    } else if (timeSeconds < 3600) {
      const secondsWithPotentialLeadingZero =
        seconds < 10 ? `0${seconds}` : `${seconds}`;
      timeString = `${minutes}:${secondsWithPotentialLeadingZero}`;
    } else {
      const secondsWithPotentialLeadingZero =
        seconds < 10 ? `0${seconds}` : `${seconds}`;
      const minutesWithPotentialLeadingZero =
        minutes < 10 ? `0${minutes}` : `${minutes}`;
      timeString = `${hours}:${minutesWithPotentialLeadingZero}:${secondsWithPotentialLeadingZero}`;
    }
    retValue = days > 0 ? `${days}${daysUnit} ${timeString}` : timeString;
    retValue = retValue.trim();
  }
  return retValue;
};

// Returns an array of years counting backwards from the current year amount [pastYears]
// Used in wizard step 4 for LastMaintenance & dealer search filters
export const getPastYearsList = (pastYears: number): number[] => {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let index = currentYear; index > currentYear - pastYears; index--) {
    years.push(index);
  }
  return years;
};

type MonthData = {
  number: number;
  name: string;
};

interface GetMonthListProps {
  includeFutureMonths?: boolean;
  year?: number;
}
export const getMonthsList = ({
  includeFutureMonths = true,
  year
}: GetMonthListProps): MonthData[] => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const months: MonthData[] = [
    {
      number: 1,
      name: t("general.months.january")
    },
    {
      number: 2,
      name: t("general.months.february")
    },
    {
      number: 3,
      name: t("general.months.march")
    },
    {
      number: 4,
      name: t("general.months.april")
    },
    {
      number: 5,
      name: t("general.months.may")
    },
    {
      number: 6,
      name: t("general.months.june")
    },
    {
      number: 7,
      name: t("general.months.july")
    },
    {
      number: 8,
      name: t("general.months.august")
    },
    {
      number: 9,
      name: t("general.months.september")
    },
    {
      number: 10,
      name: t("general.months.october")
    },
    {
      number: 11,
      name: t("general.months.november")
    },
    {
      number: 12,
      name: t("general.months.december")
    }
  ];
  const retValue: MonthData[] = [];

  months.forEach((month) => {
    if (includeFutureMonths === false) {
      if (year !== undefined && year === currentYear) {
        if (month.number <= currentMonth) {
          retValue.push(month);
        }
      } else {
        retValue.push(month);
      }
    } else {
      retValue.push(month);
    }
  });
  return retValue;
};

export const getMonthNameByNumber = (monthNumber: number): string => {
  const months = getMonthsList({ includeFutureMonths: true });
  const month = months.find((month) => month.number === monthNumber);
  return month ? month.name : "";
};

export const getMonthYearOptions = (pastYears: number): string[] => {
  const years = getPastYearsList(pastYears);
  const monthsAndYears: string[] = [];

  years.forEach((year) => {
    const months = getMonthsList({
      includeFutureMonths: false,
      year
    }).reverse();
    months.forEach((month) => {
      monthsAndYears.push(`${month.name} ${year}`);
    });
  });

  return monthsAndYears;
};

export const generateYearOptions = () => {
  return Array.from({ length: new Date().getFullYear() - 1900 + 2 }, (_, i) =>
    String(new Date().getFullYear() + 1 - i)
  );
};

export const getDateStringDMY = (date: string) => {
  const dateObj = new Date(date);
  return `${dateObj.getDate()}.${
    dateObj.getMonth() + 1
  }.${dateObj.getFullYear()}`;
};

export const getDateStringDM = (date: string) => {
  const dateObj = new Date(date);
  return `${dateObj.getDate()}.${dateObj.getMonth() + 1}`;
};

export const getTimeString = (date: string) => {
  const dateObj = new Date(date);
  return `${dateObj.getHours()}:${String(dateObj.getMinutes()).padStart(
    2,
    "0"
  )}`;
};

export const parseDateString = (value: string, originalValue: string) => {
  const parsedDate = new Date(originalValue);
  return isDate(parsedDate) && !isNaN(parsedDate.getTime())
    ? parsedDate
    : new Date("");
};

/**
 * Converts a timestamp in milliseconds to seconds.
 * @param milliseconds - The timestamp in milliseconds.
 * @returns The timestamp in seconds.
 */
export const convertMsToSec = (milliseconds: number): number => {
  return Math.floor(milliseconds / 1000);
};
